import {ConversationCountsProvider} from '@/components/_domain/SidebarItemBadges/ConversationsBadge/ConversationCountsContext';
import {BrandLoader} from '@/components/brand/BrandLoader';
import {useBrand} from '@/contexts/BrandContext';
import {CommunityGroupProvider} from '@/contexts/CommunityGroupProvider';
import {DetailSidebarProvider} from '@/contexts/DetailSidebarContext';
import {SideBarAccordionProvider} from '@/contexts/SideBarAccordionContext';
import {
  SidebarItem,
  SidebarProvider,
} from '@/contexts/SidebarContext/SidebarContext';
import {SidebarCategory} from '@/contexts/SidebarContext/types';
import {TeamProvider} from '@/contexts/TeamProvider';
import {useUserTypeContext} from '@/contexts/UserTypeContext/UserTypeContext';
import {
  LeanUnreadNotificationsCountDocument,
  NotificationCenterNotificationsDocument,
  NotificationHookFragment,
  NotificationsDocument,
  Session_Target,
  UnreadNotificationsCountDocument,
  useAllTeamsQuery,
  useCreateOrUpdateUserSessionMutation,
  useReadNotificationMutation,
} from '@/generated/graphql';
import {useFeatures} from '@/hooks/useFeatures/useFeatures';
import {NotificationContext} from '@/hooks/useInitializeNotifications';
import {useMFA} from '@/hooks/useMFA';
import {usePeriodicMutation} from '@/hooks/userPeriodicMutation';
import PageContainer from '@/layout/PageContainer';
import InitialSetup from '@/pages/ENT/ProfilePage/MFASetup/InitialSetupDialog';
import {getNotificationRoute} from '@/utils/notifications';
import {writeLeanNotificationReadState} from '@/utils/notificationCache';
import {Identify, identify} from '@amplitude/analytics-browser';
import {CommunityProvider, edgesToNodes} from '@ark/react';
import {
  CommunityFilterProvider,
  ConfettiContainer,
  EmptyList,
  UserProfileProvider,
} from '@ark/web';
import confettiContext, {ConfettiProvider} from '@landlord/contexts/Confetti';
import {HeadsUpProvider} from '@landlord/contexts/headsUpModal';
import {compact} from 'lodash';
import React, {useCallback, useEffect, useMemo} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useIntercom} from 'react-use-intercom';
import AdminRoot from './Admin/AdminRoot';
import ConciergeRoot from './Concierge/ConciergeRoot';
import {useConciergeCategoryItems} from './Concierge/ConciergeSidebarContent';
import LandlordRoot from './Landlord/LandlordRoot';
import ManagerRoot from './Manager/ManagerRoot';
import {
  useAdminItems,
  useManagerCategoryItems,
  useReportingItems,
} from './Manager/ManagerSidebarContent';
import {RMCRoot} from './RMC/RMCRoot';

const AppConfetti = () => {
  const {confettiItem, setConfettiItem} = React.useContext(confettiContext);
  return (
    <ConfettiContainer
      active={Boolean(confettiItem)}
      numPops={confettiItem?.numPops ?? 3}
      onDismiss={() => setConfettiItem(null)}
    />
  );
};

const ManagerShell = ({
  isAdmin,
  openDialog,
  onClose,
}: {
  isAdmin: boolean;
  openDialog: boolean;
  onClose: () => void;
}) => {
  const categoryItems = useManagerCategoryItems();
  return (
    <SidebarProvider categoryItems={categoryItems} defaultCategory="dashboard">
      <DetailSidebarProvider>
        <ConversationCountsProvider>
          <ManagerRoot isAdmin={isAdmin} />
          <InitialSetup onClose={onClose} open={openDialog} />
        </ConversationCountsProvider>
      </DetailSidebarProvider>
    </SidebarProvider>
  );
};

const ConciergeShell = ({
  openDialog,
  onClose,
}: {
  openDialog: boolean;
  onClose: () => void;
}) => {
  const categoryItems = useConciergeCategoryItems();
  return (
    <SidebarProvider categoryItems={categoryItems} defaultCategory="concierge">
      <DetailSidebarProvider>
        <ConversationCountsProvider>
          <ConciergeRoot />
          <InitialSetup onClose={onClose} open={openDialog} />
        </ConversationCountsProvider>
      </DetailSidebarProvider>
    </SidebarProvider>
  );
};

const AuthenticatedRoot: React.FC = () => {
  const {clientAdmin, manager, concierge, rmcDirector, email, loading} =
    useUserTypeContext();
  const {data: teamsData} = useAllTeamsQuery();
  const teams = edgesToNodes(teamsData?.teams?.edges) ?? [];
  const {update} = useIntercom();
  const navigate = useNavigate();
  const location = useLocation();
  const {setBtlSession} = useBrand();
  const isBtl =
    manager?.client?.clientType === 'BTL' ||
    concierge?.client?.clientType === 'BTL';

  useEffect(() => {
    if (!loading) setBtlSession(isBtl);
  }, [loading, isBtl, setBtlSession]);

  const [readNotification] = useReadNotificationMutation({
    update: (cache, {data}) =>
      writeLeanNotificationReadState(
        cache,
        data?.readNotification?.notification?.id,
        true,
      ),
    refetchQueries: [
      {query: NotificationCenterNotificationsDocument, variables: {}},
      {query: LeanUnreadNotificationsCountDocument},
      {query: NotificationsDocument, variables: {read: false}},
      {query: UnreadNotificationsCountDocument},
    ],
  });

  const handleNotificationClick = useCallback(
    (notification: NotificationHookFragment) => {
      readNotification({
        variables: {input: {id: notification.id}},
        optimisticResponse: {
          readNotification: {
            __typename: 'ReadNotificationPayload',
            notification: {
              __typename: 'NotificationNode',
              id: notification.id,
              read: true,
            },
          },
        },
      }).catch(() => {});
      const route = getNotificationRoute(notification);
      if (route) navigate(route);
    },
    [navigate, readNotification],
  );

  const shouldSuppressNotification = useCallback(
    (notification: NotificationHookFragment) => {
      const route = getNotificationRoute(notification);
      return route ? location.pathname.startsWith(route) : false;
    },
    [location.pathname],
  );
  const adminItems = useAdminItems();
  const reportingItems = useReportingItems();
  const adminCategoryItems = useMemo(
    () =>
      new Map<SidebarCategory, SidebarItem[]>([
        ['reporting', reportingItems],
        ['admin', adminItems],
      ]),
    [reportingItems, adminItems],
  );
  const interval = 5 * 60 * 1000;
  const [mutate] = useCreateOrUpdateUserSessionMutation({
    variables: {input: {sessionTarget: 'MANAGER_DASHBOARD' as Session_Target}},
    ignoreResults: true,
  });
  usePeriodicMutation(mutate, interval);

  useEffect(() => {
    const identifyObj = new Identify();
    if (email) identifyObj.set('email', email);
    if (
      manager?.client?.clientType === 'ENT' ||
      concierge?.client?.clientType === 'ENT'
    ) {
      const client = manager?.client?.name ?? concierge?.client?.name;
      if (client) identifyObj.postInsert('Client', client);
    }
    if (clientAdmin) identifyObj.postInsert('Permission Levels', 'Admin');
    if (manager) identifyObj.postInsert('Permission Levels', 'Manager');
    if (concierge) identifyObj.postInsert('Permission Levels', 'Concierge');
    identify(identifyObj);
  }, [clientAdmin, manager, concierge, email]);

  useEffect(() => {
    if (manager?.email) {
      update({
        email: manager.email,
      });
    }
  }, [update, manager]);

  const [{MFA}] = useFeatures();
  const {mfaDevices, initialized} = useMFA();
  const [openDialog, setOpenDialog] = React.useState(false);

  const combinedCommunities = useMemo(() => {
    const managerCommunities = edgesToNodes(manager?.community?.edges) ?? [];
    const adminCommunities = clientAdmin
      ? (edgesToNodes(clientAdmin.communities?.edges) ?? [])
      : [];
    return [
      ...managerCommunities,
      ...adminCommunities.filter(
        ac => !managerCommunities.some(mc => mc.id === ac.id),
      ),
    ].sort((a, b) => a.name.localeCompare(b.name));
  }, [manager?.community?.edges, clientAdmin]);

  const onClose = async () => {
    setOpenDialog(false);
  };

  React.useEffect(() => {
    if (initialized && MFA && !mfaDevices.app && !mfaDevices.text) {
      setOpenDialog(true);
    }
  }, [MFA, setOpenDialog, mfaDevices, initialized]);

  if (loading) {
    return <BrandLoader />;
  }

  if (manager && manager?.client?.clientType === 'BTL') {
    return (
      <UserProfileProvider>
        <HeadsUpProvider>
          <ConfettiProvider>
            <LandlordRoot />
            <AppConfetti />
          </ConfettiProvider>
        </HeadsUpProvider>
      </UserProfileProvider>
    );
  }

  if (rmcDirector) {
    return (
      <UserProfileProvider>
        <CommunityProvider
          communities={edgesToNodes(rmcDirector.communities?.edges)}
        >
          <CommunityFilterProvider>
            <RMCRoot />
          </CommunityFilterProvider>
        </CommunityProvider>
      </UserProfileProvider>
    );
  }

  if (
    manager &&
    manager?.client?.clientType === 'ENT' &&
    (manager?.community?.totalCount || 0) > 0
  ) {
    return (
      <UserProfileProvider>
        <NotificationContext
          onNotificationClick={handleNotificationClick}
          shouldSuppress={shouldSuppressNotification}
        >
          <CommunityProvider communities={combinedCommunities}>
            <CommunityFilterProvider>
              <CommunityGroupProvider>
                <TeamProvider teams={teams}>
                  <SideBarAccordionProvider>
                    <ManagerShell
                      isAdmin={Boolean(clientAdmin)}
                      openDialog={openDialog}
                      onClose={onClose}
                    />
                  </SideBarAccordionProvider>
                </TeamProvider>
              </CommunityGroupProvider>
            </CommunityFilterProvider>
          </CommunityProvider>
        </NotificationContext>
      </UserProfileProvider>
    );
  }

  if (clientAdmin) {
    return (
      <UserProfileProvider>
        <NotificationContext
          onNotificationClick={handleNotificationClick}
          shouldSuppress={shouldSuppressNotification}
        >
          <CommunityProvider
            communities={edgesToNodes(clientAdmin?.communities?.edges)}
          >
            <CommunityFilterProvider>
              <TeamProvider teams={teams}>
                <SidebarProvider
                  categoryItems={adminCategoryItems}
                  defaultCategory="admin"
                >
                  <DetailSidebarProvider>
                    <ConversationCountsProvider>
                      <AdminRoot />
                      <InitialSetup onClose={onClose} open={openDialog} />
                    </ConversationCountsProvider>
                  </DetailSidebarProvider>
                </SidebarProvider>
              </TeamProvider>
            </CommunityFilterProvider>
          </CommunityProvider>
        </NotificationContext>
      </UserProfileProvider>
    );
  }

  if (concierge && (concierge?.communities?.totalCount || 0) > 0) {
    return (
      <UserProfileProvider>
        <NotificationContext
          onNotificationClick={handleNotificationClick}
          shouldSuppress={shouldSuppressNotification}
        >
          <CommunityProvider
            communities={compact(edgesToNodes(concierge.communities?.edges))}
          >
            <CommunityFilterProvider>
              <CommunityGroupProvider>
                <TeamProvider teams={teams}>
                  <SideBarAccordionProvider>
                    <ConciergeShell openDialog={openDialog} onClose={onClose} />
                  </SideBarAccordionProvider>
                </TeamProvider>
              </CommunityGroupProvider>
            </CommunityFilterProvider>
          </CommunityProvider>
        </NotificationContext>
      </UserProfileProvider>
    );
  }

  return (
    <PageContainer>
      <EmptyList text="You are not currently assigned to any communities, please get in touch with your administrator" />
    </PageContainer>
  );
};

export default AuthenticatedRoot;
